import { useEffect, useRef, useState } from 'react';

// A cumulative series: the value at each time, in ms.
export type CounterPoint = { tMs: number; v: number };

/**
 * Piecewise-linear interpolation through the breakpoints. Past the last
 *  breakpoint, returns the last value unchanged (no extrapolation). The display
 *  freezes there until the next fetch brings a fresh point.
 *
 *  `stalled` is true when the two breakpoints bracketing the display time hold
 *  the same cumulative total — nothing was injected across that interval, so the
 *  counter is holding steady rather than ticking up.
 */
export function sampleAt(
  series: CounterPoint[],
  targetMs: number,
): { value: number | null; stalled: boolean } {
  if (series.length === 0)
    return { value: null, stalled: false };
  if (targetMs <= series[0]!.tMs)
    return { value: series[0]!.v, stalled: false };
  for (let i = series.length - 1; i >= 0; i--) {
    const a = series[i]!;
    if (a.tMs <= targetMs) {
      const b = series[i + 1];
      if (!b)
        return { value: a.v, stalled: false };
      const f = (targetMs - a.tMs) / (b.tMs - a.tMs);
      return { value: a.v + f * (b.v - a.v), stalled: a.v === b.v };
    }
  }
  return { value: series[series.length - 1]!.v, stalled: false };
}

/**
 * How far behind real time we display. Must be >= the worst-case CAGG-lag gap
 * + a buffer that covers fetch-to-fetch jitter and the occasional missed refresh
 * tick. CAGG `end_offset=1m` + ~30s schedule jitter puts the newest closed
 * minute at T-2:00 to T-3:00; 300s gives ~1–2 ticks of headroom before the
 * display would outrun the series and freeze. 5 minutes of lag is invisible on a
 * public-facing landing-page counter.
 */
const SAFETY_LAG_MS = 300_000;

/**
 * The series' value at server time `serverNow` + time since it changed − the lag, each frame, so it counts up
 * smoothly between fetches. It never goes down until `epoch` changes (a series on a new scale). `step` rounds the
 * value down before it is set, so a component re-renders only when the shown value changes. `series` must keep its
 * identity between renders (memoize it).
 */
export function useTicking(series: CounterPoint[], serverNow: string | undefined, { step, epoch }: { step?: number; epoch?: unknown } = {}) {
  // Time anchor: captured on each fetch as (clientNow, serverNow). Display
  // server-time = serverAtFetch + (Date.now() - clientAtFetch) - SAFETY_LAG_MS.
  const anchorRef = useRef<{ clientAtFetch: number; serverAtFetch: number } | null>(null);
  useEffect(() => {
    if (serverNow)
      anchorRef.current = { clientAtFetch: Date.now(), serverAtFetch: new Date(serverNow).getTime() };
  }, [serverNow]);

  // Defensive monotone clamp: even if interpolation hiccups, the displayed value never goes down.
  const lastShownRef = useRef<number | null>(null);
  useEffect(() => {
    lastShownRef.current = null;
  }, [epoch]);

  const [value, setValue] = useState<number | null>(null);
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const anchor = anchorRef.current;
      if (anchor && series.length > 0) {
        const sample = sampleAt(series, anchor.serverAtFetch + (Date.now() - anchor.clientAtFetch) - SAFETY_LAG_MS);
        if (sample.value != null) {
          const monotone = lastShownRef.current != null ? Math.max(sample.value, lastShownRef.current) : sample.value;
          lastShownRef.current = monotone;
          setValue(step != null && step > 0 ? Math.floor(monotone / step) * step : monotone);
          setStalled(sample.stalled);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [series, step]);

  return { value, stalled };
}
